import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bug, Lightbulb, Upload, X, Loader2, CheckCircle } from 'lucide-react';
import { api } from '@/api';
import { toast } from 'sonner';

const BUG_SEVERITIES = [
  { value: 'blocker', label: 'Blocker - Cannot use the system', color: 'text-red-600' },
  { value: 'major', label: 'Major - Important feature broken', color: 'text-orange-600' },
  { value: 'minor', label: 'Minor - Inconvenience', color: 'text-yellow-600' },
  { value: 'cosmetic', label: 'Cosmetic - Visual issue only', color: 'text-blue-600' },
];

const CATEGORIES = [
  { value: 'ui_ux', label: 'UI/UX' },
  { value: 'backend', label: 'Backend/API' },
  { value: 'performance', label: 'Performance' },
  { value: 'security', label: 'Security' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'integration', label: 'Integration' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'other', label: 'Other' },
];

export function FeedbackButtons() {
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setBugDialogOpen(true)}
          className="text-slate-600 hover:text-red-600 hover:bg-red-50"
          data-testid="report-bug-btn"
        >
          <Bug className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Report Bug</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFeatureDialogOpen(true)}
          className="text-slate-600 hover:text-blue-600 hover:bg-blue-50"
          data-testid="request-feature-btn"
        >
          <Lightbulb className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Request Feature</span>
        </Button>
      </div>

      <BugReportDialog open={bugDialogOpen} onOpenChange={setBugDialogOpen} />
      <FeatureRequestDialog open={featureDialogOpen} onOpenChange={setFeatureDialogOpen} />
    </>
  );
}

function BugReportDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'minor',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    category: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in the title and description');
      return;
    }

    setLoading(true);
    try {
      // Add browser info automatically
      const browserInfo = `${navigator.userAgent} | Screen: ${window.screen.width}x${window.screen.height} | URL: ${window.location.href}`;
      
      const response = await api.feedback.reportBug({
        ...formData,
        browser_info: browserInfo,
      });

      const newTicketId = response.data.ticket_id;
      setTicketNumber(response.data.ticket_number);

      // Upload attachments if any
      if (attachments.length > 0) {
        for (const file of attachments) {
          try {
            await api.feedback.uploadAttachment(newTicketId, file);
          } catch (err) {
            console.error('Failed to upload attachment:', err);
          }
        }
      }

      setSuccess(true);
      toast.success(`Bug report #${response.data.ticket_number} submitted successfully`);
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit bug report');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB`);
        return false;
      }
      return true;
    });
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      severity: 'minor',
      steps_to_reproduce: '',
      expected_behavior: '',
      actual_behavior: '',
      category: '',
    });
    setAttachments([]);
    setSuccess(false);
    setTicketNumber(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Bug className="h-5 w-5" />
            Report a Bug
          </DialogTitle>
          <DialogDescription>
            Help us improve by reporting issues you encounter
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Bug Report Submitted!</h3>
            <p className="text-slate-600 mb-4">
              Your ticket number is <span className="font-mono font-bold">#{ticketNumber}</span>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              You can track the status in your Help Portal
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="bug-title">Title *</Label>
              <Input
                id="bug-title"
                placeholder="Brief description of the bug"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bug-severity">Severity</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                  <SelectTrigger id="bug-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUG_SEVERITIES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        <span className={s.color}>{s.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="bug-category">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger id="bug-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="bug-description">Description *</Label>
              <Textarea
                id="bug-description"
                placeholder="Detailed description of what went wrong"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="bug-steps">Steps to Reproduce</Label>
              <Textarea
                id="bug-steps"
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bug-expected">Expected Behavior</Label>
                <Textarea
                  id="bug-expected"
                  placeholder="What should happen?"
                  value={formData.expected_behavior}
                  onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="bug-actual">Actual Behavior</Label>
                <Textarea
                  id="bug-actual"
                  placeholder="What actually happened?"
                  value={formData.actual_behavior}
                  onChange={(e) => setFormData({ ...formData, actual_behavior: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Screenshot Upload */}
            <div>
              <Label>Screenshots (optional)</Label>
              <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="bug-attachments"
                />
                <label
                  htmlFor="bug-attachments"
                  className="flex flex-col items-center cursor-pointer text-slate-500 hover:text-slate-700"
                >
                  <Upload className="h-8 w-8 mb-2" />
                  <span className="text-sm">Click to upload screenshots</span>
                  <span className="text-xs text-slate-400">PNG, JPEG, GIF, WebP, PDF (max 5MB each)</span>
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                      <span className="text-sm truncate">{file.name}</span>
                      <button type="button" onClick={() => removeAttachment(index)} className="text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bug className="h-4 w-4 mr-2" />}
                Submit Bug Report
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FeatureRequestDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    use_case: '',
    business_value: '',
    category: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in the title and description');
      return;
    }

    setLoading(true);
    try {
      const response = await api.feedback.requestFeature(formData);
      setTicketNumber(response.data.ticket_number);
      setSuccess(true);
      toast.success(`Feature request #${response.data.ticket_number} submitted successfully`);
    } catch (error) {
      console.error('Failed to submit feature request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feature request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      use_case: '',
      business_value: '',
      category: '',
    });
    setSuccess(false);
    setTicketNumber(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <Lightbulb className="h-5 w-5" />
            Request a Feature
          </DialogTitle>
          <DialogDescription>
            Share your ideas to help us improve the platform
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Feature Request Submitted!</h3>
            <p className="text-slate-600 mb-4">
              Your ticket number is <span className="font-mono font-bold">#{ticketNumber}</span>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Other users can vote for your feature on the Roadmap page
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="feature-title">Feature Title *</Label>
              <Input
                id="feature-title"
                placeholder="Brief title for your feature request"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="feature-category">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger id="feature-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="feature-description">Description *</Label>
              <Textarea
                id="feature-description"
                placeholder="Describe the feature you'd like to see"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div>
              <Label htmlFor="feature-use-case">Use Case</Label>
              <Textarea
                id="feature-use-case"
                placeholder="How would you use this feature? What problem does it solve?"
                value={formData.use_case}
                onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="feature-value">Business Value</Label>
              <Textarea
                id="feature-value"
                placeholder="How would this feature benefit you or your team?"
                value={formData.business_value}
                onChange={(e) => setFormData({ ...formData, business_value: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lightbulb className="h-4 w-4 mr-2" />}
                Submit Feature Request
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackButtons;
